import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api, { employeesAPI } from '../../../lib/api';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from '../../../components/ui/select';
import { Send, Loader2, MessageSquare, Megaphone, Users, Wrench, Image as ImageIcon, Eye, Pin } from 'lucide-react';
import PhotoAttach from '../../reviewShield/PhotoAttach';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../../components/ui/alert-dialog';

const VALID_CAPS = ['housekeeping', 'security', 'concierge', 'valet', 'hotel_manager',
    'maintenance', 'parking', 'waste_recycling', 'amenities', 'building', 'management'];

// Derive a floor label from a unit/room number (reuses existing room data;
// e.g. 101/102 → "1", 201 → "2", condo A-1xx → "1"). Replaceable later by a
// real `floor` field on hotel_rooms without touching this UI.
const unitFloor = (rn) => {
    const digits = String(rn || '').replace(/\D/g, '');
    if (digits.length >= 3) return String(parseInt(digits.slice(0, digits.length - 2), 10));
    return null;
};

/** Send a guest/resident message or community notice from Command Center. */
export function CommunicationDrawer({ open, onOpenChange, vertical, locations, defaultLocationId, onSent }) {
    const { t } = useTranslation();
    const isCondo = vertical === 'condo';
    const [locationId, setLocationId] = useState(defaultLocationId || (locations[0]?.id));
    const [channel, setChannel] = useState(isCondo ? 'community_board' : 'message');
    const [priority, setPriority] = useState(false);
    const [requireAck, setRequireAck] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [units, setUnits] = useState([]);
    const [recipient, setRecipient] = useState('all');

    // Initialize on OPEN only — background refresh prop changes must not reset
    // the draft (message/title/recipient) the user is composing.
    useEffect(() => {
        if (open) {
            setLocationId(defaultLocationId || locations[0]?.id);
            setChannel(isCondo ? 'community_board' : 'message');
            setPriority(false); setRequireAck(false); setTitle(''); setMessage('');
            setRecipient('all');
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load unit/room list for the selected property/building (recipient targeting).
    useEffect(() => {
        if (!open || !locationId) { setUnits([]); return; }
        setRecipient('all');
        api.get('/command-center/units', { params: { location_id: locationId } })
            .then((r) => setUnits(r.data?.units || []))
            .catch(() => setUnits([]));
    }, [open, locationId]);

    // Group units by floor (real floor field when present, else derived).
    const floorMap = {};
    units.forEach((u) => {
        const f = (u.floor != null && String(u.floor).trim()) ? String(u.floor).trim() : unitFloor(u.room_number);
        if (f) { (floorMap[f] = floorMap[f] || []).push(u.room_number); }
    });
    const floors = Object.keys(floorMap).sort((a, b) => Number(a) - Number(b));

    const resolveTarget = () => {
        if (recipient.startsWith('floor:')) return { all_rooms: false, rooms: floorMap[recipient.slice(6)] || [] };
        if (recipient.startsWith('unit:')) return { all_rooms: false, rooms: [recipient.slice(5)] };
        return { all_rooms: true, rooms: [] };
    };
    const targetCount = (() => { const tg = resolveTarget(); return tg.all_rooms ? units.length : tg.rooms.length; })();

    const submit = async () => {
        if (!message.trim()) { toast.error(t('commandCenter.act.messageRequired')); return; }
        const tg = resolveTarget();
        setSending(true);
        try {
            const res = await api.post('/command-center/act/communication', {
                location_id: locationId,
                type: 'message',
                channel,
                all_rooms: tg.all_rooms,
                rooms: tg.rooms,
                priority: priority ? 'priority' : 'normal',
                audience: channel === 'community_board' ? 'hotel' : 'guest',
                require_ack: channel === 'community_board' ? requireAck : false,
                title: title.trim() || null,
                message: message.trim(),
                translate: true,
                source_language: 'en',
            });
            toast.success(t('commandCenter.act.sent', { count: res.data?.delivered ?? 0 }));
            onOpenChange(false);
            onSent && onSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cc-comm-drawer">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send className="w-4 h-4 text-emerald-500" />{t('commandCenter.act.communicate')}
                    </DialogTitle>
                    <DialogDescription data-testid="cc-comm-recipient-summary">{t('commandCenter.act.recipientsSummary', { count: targetCount })}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    {locations.length > 1 && (
                        <div className="space-y-1.5">
                            <Label className="text-xs">{t('commandCenter.act.recipientProperty')}</Label>
                            <Select value={locationId} onValueChange={setLocationId}>
                                <SelectTrigger data-testid="cc-comm-location"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="space-y-1.5">
                        <Label className="text-xs">{t('commandCenter.act.recipients')}</Label>
                        <Select value={recipient} onValueChange={setRecipient}>
                            <SelectTrigger data-testid="cc-comm-recipient"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="all" data-testid="cc-comm-recipient-all">{t('commandCenter.act.allUnits')}</SelectItem>
                                {floors.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>{t('commandCenter.act.recipientsByFloor')}</SelectLabel>
                                        {floors.map((f) => (
                                            <SelectItem key={`floor-${f}`} value={`floor:${f}`} data-testid={`cc-comm-recipient-floor-${f}`}>
                                                {t('commandCenter.act.recipientFloor', { floor: f })}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                                {units.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>{t('commandCenter.act.recipientsByUnit')}</SelectLabel>
                                        {units.map((u) => (
                                            <SelectItem key={`unit-${u.room_number}`} value={`unit:${u.room_number}`} data-testid={`cc-comm-recipient-unit-${u.room_number}`}>
                                                {t('commandCenter.act.recipientUnit', { unit: u.room_number })}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setChannel('message')} data-testid="cc-comm-channel-message"
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${channel === 'message' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border'}`}>
                            <MessageSquare className="w-4 h-4" />{t('commandCenter.act.channelMessage')}
                        </button>
                        <button type="button" onClick={() => setChannel('community_board')} data-testid="cc-comm-channel-notice"
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${channel === 'community_board' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border'}`}>
                            <Megaphone className="w-4 h-4" />{t('commandCenter.act.channelNotice')}
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">{t('commandCenter.act.titleField')}</Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="cc-comm-title" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs">{t('commandCenter.act.messageField')}</Label>
                        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="cc-comm-message" />
                    </div>
                    <div className="flex items-center justify-between">
                        <Label className="text-sm">{t('commandCenter.act.priority')}</Label>
                        <Switch checked={priority} onCheckedChange={setPriority} data-testid="cc-comm-priority" />
                    </div>
                    {channel === 'community_board' && (
                        <div className="flex items-center justify-between">
                            <Label className="text-sm">{t('commandCenter.act.requireAck')}</Label>
                            <Switch checked={requireAck} onCheckedChange={setRequireAck} data-testid="cc-comm-requireack" />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cc-comm-cancel">{t('commandCenter.act.cancel')}</Button>
                    <Button onClick={submit} disabled={sending} data-testid="cc-comm-send">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                        {sending ? t('commandCenter.act.sending') : t('commandCenter.act.send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Assign staff department capabilities from Command Center. */
export function AssignStaffDrawer({ open, onOpenChange, locations, defaultLocationId }) {
    const { t } = useTranslation();
    const [locationId, setLocationId] = useState(defaultLocationId || locations[0]?.id);
    const [employees, setEmployees] = useState([]);
    const [empId, setEmpId] = useState('');
    const [caps, setCaps] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLocationId(defaultLocationId || locations[0]?.id);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !locationId) return;
        employeesAPI.list(locationId).then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
    }, [open, locationId]);

    useEffect(() => {
        const e = employees.find((x) => x.id === empId);
        setCaps(e?.hotel_capabilities || []);
    }, [empId, employees]);

    const toggleCap = (c) => setCaps((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);

    const save = async () => {
        if (!empId) return;
        setSaving(true);
        try {
            await api.post('/command-center/act/assign-capabilities', { employee_id: empId, hotel_capabilities: caps });
            toast.success(t('commandCenter.act.saved'));
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="cc-assign-drawer">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-500" />{t('commandCenter.act.assignStaff')}
                    </DialogTitle>
                    <DialogDescription>{t('commandCenter.act.capabilities')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    {locations.length > 1 && (
                        <Select value={locationId} onValueChange={setLocationId}>
                            <SelectTrigger data-testid="cc-assign-location"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <div className="space-y-1.5">
                        <Label className="text-xs">{t('commandCenter.act.selectEmployee')}</Label>
                        {employees.length === 0 ? (
                            <p className="text-sm text-muted-foreground" data-testid="cc-assign-noemp">{t('commandCenter.act.noEmployees')}</p>
                        ) : (
                            <Select value={empId} onValueChange={setEmpId}>
                                <SelectTrigger data-testid="cc-assign-employee"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    {empId && (
                        <div className="space-y-2">
                            <Label className="text-xs">{t('commandCenter.act.capabilities')}</Label>
                            <div className="flex flex-wrap gap-2">
                                {VALID_CAPS.map((c) => (
                                    <button key={c} type="button" onClick={() => toggleCap(c)} data-testid={`cc-assign-cap-${c}`}
                                        className={`rounded-full border px-3 py-1 text-xs ${caps.includes(c) ? 'border-purple-500 bg-purple-500/15 text-purple-300' : 'border-border text-muted-foreground'}`}>
                                        {t(`commandCenter.act.cap.${c}`, c.replace(/_/g, ' '))}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('commandCenter.act.cancel')}</Button>
                    <Button onClick={save} disabled={saving || !empId} data-testid="cc-assign-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}{t('commandCenter.act.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/** Manager-initiated Dispatch Task — creates a request record, assigns staff, notifies via SMS. */
const CATEGORY_OPTIONS = {
    condo: [
        ['maintenance', 'concierge'], ['security', 'security'], ['parking', 'concierge'],
        ['waste_recycling', 'concierge'], ['amenities', 'concierge'], ['building', 'concierge'], ['other', 'concierge'],
    ],
    hotel: [
        ['maintenance', 'concierge'], ['security', 'security'], ['concierge', 'concierge'],
        ['housekeeping', 'concierge'], ['other', 'concierge'],
    ],
    // Automotive sales-floor dispatch reuses the concierge task engine (SMS +
    // Staff Hub); salespeople are `employees` with the 'concierge' capability.
    automotive: [
        ['sales', 'concierge'], ['delivery', 'concierge'], ['service', 'concierge'], ['other', 'concierge'],
    ],
};

export function DispatchTaskDrawer({ open, onOpenChange, vertical, locations, defaultLocationId, onSent, presetMessage }) {
    const { t } = useTranslation();
    const opts = CATEGORY_OPTIONS[vertical] || CATEGORY_OPTIONS.hotel;
    const [locationId, setLocationId] = useState(defaultLocationId || locations[0]?.id);
    const [category, setCategory] = useState(opts[0][0]);
    const [room, setRoom] = useState('');
    const [message, setMessage] = useState('');
    const [urgent, setUrgent] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [empId, setEmpId] = useState('');
    const [sending, setSending] = useState(false);

    const kind = (opts.find((o) => o[0] === category) || opts[0])[1];

    useEffect(() => {
        if (!open) return;
        setLocationId(defaultLocationId || locations[0]?.id);
        setCategory(opts[0][0]); setRoom(''); setMessage(presetMessage || ''); setUrgent(false); setEmpId('');
    }, [open]); // eslint-disable-line

    useEffect(() => {
        if (!open || !locationId) return;
        employeesAPI.list(locationId).then((r) => setEmployees(r.data || [])).catch(() => setEmployees([]));
    }, [open, locationId]);

    // Only staff who can act on this task kind (capability-gated Staff Hub) are assignable.
    const eligible = employees.filter((e) => (e.hotel_capabilities || []).includes(kind));

    const dispatch = async () => {
        if (!locationId || !message.trim()) { toast.error(t('commandCenter.act.dispatch.needDesc')); return; }
        setSending(true);
        try {
            const res = await api.post('/command-center/act/dispatch', {
                location_id: locationId, kind, category, room_number: room.trim() || null,
                message: message.trim(), assigned_employee_id: empId || null, urgent: kind === 'security' ? urgent : false,
            });
            toast.success(res.data?.notified ? t('commandCenter.act.dispatch.sentNotified') : t('commandCenter.act.dispatch.sent'));
            onOpenChange(false);
            onSent && onSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.dispatch.failed'));
        } finally { setSending(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cc-dispatch-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Wrench className="w-4 h-4 text-amber-400" />{t('commandCenter.act.dispatch.title')}</DialogTitle>
                    <DialogDescription>{t('commandCenter.act.dispatch.subtitle')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.selectLocation')}</Label>
                        <Select value={locationId} onValueChange={setLocationId}>
                            <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-dispatch-location"><SelectValue /></SelectTrigger>
                            <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.dispatch.category')}</Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-dispatch-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {opts.map(([c]) => <SelectItem key={c} value={c}>{t(`commandCenter.act.dispatch.cat.${c}`, c.charAt(0).toUpperCase() + c.slice(1))}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.dispatch.unitRoom')}</Label>
                            <Input value={room} onChange={(e) => setRoom(e.target.value)} placeholder={vertical === 'condo' ? '1204' : '101'}
                                className="bg-slate-800 border-slate-700" data-testid="cc-dispatch-room" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.dispatch.instructions')}</Label>
                        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                            placeholder={t('commandCenter.act.dispatch.instructionsPlaceholder')}
                            className="bg-slate-800 border-slate-700" data-testid="cc-dispatch-message" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.dispatch.assignTo')}</Label>
                        {eligible.length === 0 ? (
                            <p className="text-xs text-amber-400 py-2" data-testid="cc-dispatch-no-staff">{t('commandCenter.act.dispatch.noStaff')}</p>
                        ) : (
                            <Select value={empId} onValueChange={setEmpId}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-dispatch-employee"><SelectValue placeholder={t('commandCenter.act.dispatch.unassigned')} /></SelectTrigger>
                                <SelectContent>{eligible.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                            </Select>
                        )}
                    </div>
                    {kind === 'security' && (
                        <label className="flex items-center gap-2 text-sm text-slate-300" data-testid="cc-dispatch-urgent">
                            <Switch checked={urgent} onCheckedChange={setUrgent} />{t('commandCenter.act.dispatch.urgent')}
                        </label>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('commandCenter.act.cancel')}</Button>
                    <Button onClick={dispatch} disabled={sending || !message.trim()} data-testid="cc-dispatch-send"
                        className="bg-amber-600 hover:bg-amber-500 text-white">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}{t('commandCenter.act.dispatch.send')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Reassign a request to another EMPLOYEE (person-level) or route it to another
 *  DEPARTMENT (the work actually leaves one queue and arrives in the other).
 *  The department list comes from the backend registry, so a vertical with a
 *  single department (automotive) simply shows person-level targeting. */
export function ReassignDialog({ state, onClose, onDone }) {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState([]);
    const [empId, setEmpId] = useState('');
    const [deptId, setDeptId] = useState('');
    const [depts, setDepts] = useState([]);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const open = !!state;

    useEffect(() => {
        if (!open) { setEmpId(''); setDeptId(''); setReason(''); setEmployees([]); setDepts([]); return; }
        if (state.locationId) {
            const cap = state.kind === 'security' ? 'security' : state.kind === 'valet' ? 'valet' : 'concierge';
            employeesAPI.list(state.locationId)
                .then((r) => setEmployees((r.data || []).filter((e) => (e.hotel_capabilities || []).includes(cap))))
                .catch(() => setEmployees([]));
        }
        api.get('/routing/departments', { params: { kind: state.kind, current: state.department || undefined } })
            .then((r) => setDepts(r.data?.routable ? (r.data?.departments || []) : []))
            .catch(() => setDepts([]));
    }, [open]); // eslint-disable-line

    const submit = async () => {
        if (!empId && !deptId) return;
        setBusy(true);
        try {
            const res = await api.post(`/command-center/act/request/${state.kind}/${state.id}/reassign`,
                {
                    assigned_employee_id: empId || null,
                    assigned_department: deptId || null,
                    reason: reason.trim() || null,
                });
            toast.success(res.data?.department && deptId
                ? t('commandCenter.act.routedTo', { dept: t(`commandCenter.act.dept.${res.data.department}`, res.data.department) })
                : t('commandCenter.act.reassigned'));
            onDone && onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.reassignFailed'));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cc-reassign-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4 text-sky-400" />{t('commandCenter.act.reassign')}</DialogTitle>
                    <DialogDescription>
                        {state?.current ? t('commandCenter.act.reassignFrom', { name: state.current }) : t('commandCenter.act.reassignSubtitle')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {depts.length > 0 && (
                        <div>
                            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.routeToDept')}</Label>
                            <Select value={deptId} onValueChange={setDeptId}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-reassign-department">
                                    <SelectValue placeholder={t('commandCenter.act.routeToDeptPlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>{depts.map((d) => (
                                    <SelectItem key={d.id} value={d.id} data-testid={`cc-reassign-dept-${d.id}`}>
                                        {t(`commandCenter.act.dept.${d.id}`, d.label)}
                                    </SelectItem>
                                ))}</SelectContent>
                            </Select>
                            <p className="text-[11px] text-slate-500 mt-1">{t('commandCenter.act.routeToDeptHelp')}</p>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.dispatch.assignTo')}</Label>
                        {employees.length === 0 ? (
                            <p className="text-xs text-amber-400 py-2" data-testid="cc-reassign-no-staff">{t('commandCenter.act.dispatch.noStaff')}</p>
                        ) : (
                            <Select value={empId} onValueChange={setEmpId}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-reassign-employee"><SelectValue placeholder={t('commandCenter.act.dispatch.unassigned')} /></SelectTrigger>
                                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                            </Select>
                        )}
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.reassignReason')}</Label>
                        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('commandCenter.act.reassignReasonPlaceholder')}
                            className="bg-slate-800 border-slate-700" data-testid="cc-reassign-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>{t('commandCenter.act.cancel')}</Button>
                    <Button onClick={submit} disabled={busy || (!empId && !deptId)} data-testid="cc-reassign-confirm" className="bg-sky-600 hover:bg-sky-500 text-white">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Users className="w-4 h-4 mr-1" />}{t('commandCenter.act.reassign')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/** Send a building-wide / floor announcement to residents (Group Messaging, Slice 4). */
export function GroupMessageDrawer({ open, onOpenChange, locations, defaultLocationId, onSent }) {
    const { t } = useTranslation();
    const [locationId, setLocationId] = useState(defaultLocationId || locations[0]?.id);
    const [target, setTarget] = useState('all');
    const [groups, setGroups] = useState([]);
    const [message, setMessage] = useState('');
    const [photos, setPhotos] = useState([]);
    const [pinned, setPinned] = useState(false);
    const [sending, setSending] = useState(false);
    const [recent, setRecent] = useState([]);
    const [recallItem, setRecallItem] = useState(null);
    const [recalling, setRecalling] = useState(false);

    // Initialize the draft ONLY when the drawer opens. Background refreshes
    // (parent's 5s poll) pass new locations/defaultLocationId references — those
    // must NEVER wipe the user's in-progress announcement, so they are not deps.
    useEffect(() => {
        if (open) {
            setLocationId(defaultLocationId || locations[0]?.id);
            setTarget('all'); setMessage(''); setPhotos([]); setPinned(false);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload target groups + recent announcements whenever the property changes.
    useEffect(() => {
        if (!open || !locationId) { setGroups([]); setRecent([]); return; }
        setTarget('all');
        api.get('/command-center/group-message-targets', { params: { location_id: locationId } })
            .then((r) => setGroups(r.data?.groups || [])).catch(() => setGroups([]));
        api.get('/command-center/group-messages', { params: { location_id: locationId } })
            .then((r) => setRecent(r.data?.messages || [])).catch(() => setRecent([]));
    }, [open, locationId]);

    const refreshRecent = () => api.get('/command-center/group-messages', { params: { location_id: locationId } })
        .then((r) => setRecent(r.data?.messages || [])).catch(() => {});

    const floors = groups.filter((g) => g.type === 'floor').map((g) => g.value);

    const submit = async () => {
        if (!message.trim()) { toast.error(t('commandCenter.act.announce.needMessage')); return; }
        const isFloor = target.startsWith('floor:');
        setSending(true);
        try {
            let photo_ids = [];
            if (photos.length) {
                const fd = new FormData();
                fd.append('location_id', locationId);
                photos.forEach((p) => fd.append('photos', p.file));
                const up = await api.post('/command-center/group-message-photos', fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } });
                photo_ids = up.data?.photo_ids || [];
            }
            await api.post('/command-center/group-messages', {
                location_id: locationId,
                scope_type: isFloor ? 'floor' : 'all',
                scope_value: isFloor ? Number(target.slice(6)) : null,
                body: message.trim(),
                photo_ids,
                pinned,
            });
            toast.success(t('commandCenter.act.announce.sent'));
            setMessage(''); setTarget('all'); setPhotos([]); setPinned(false);
            await refreshRecent();
            onSent && onSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.announce.failed'));
        } finally { setSending(false); }
    };

    const togglePinItem = async (m) => {
        try {
            await api.post(`/command-center/group-messages/${m.id}/pin`, { pinned: !m.pinned });
            await refreshRecent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.announce.failed'));
        }
    };

    const doRecall = async () => {
        if (!recallItem) return;
        setRecalling(true);
        try {
            await api.post(`/command-center/group-messages/${recallItem.id}/recall`);
            toast.success(t('commandCenter.act.announce.recalled'));
            setRecallItem(null);
            await refreshRecent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.announce.recallFailed'));
        } finally { setRecalling(false); }
    };

    const scopeLabel = (m) => (m.scope_type === 'floor'
        ? t('commandCenter.act.announce.floor', { floor: m.scope_value })
        : t('commandCenter.act.announce.allResidents'));

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cc-announce-drawer">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Megaphone className="w-4 h-4 text-purple-400" />{t('commandCenter.act.announce.title')}</DialogTitle>
                        <DialogDescription>{t('commandCenter.act.announce.subtitle')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-1">
                        {locations.length > 1 && (
                            <div className="space-y-1.5">
                                <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.announce.property')}</Label>
                                <Select value={locationId} onValueChange={setLocationId}>
                                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-announce-location"><SelectValue /></SelectTrigger>
                                    <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.announce.sendTo')}</Label>
                            <Select value={target} onValueChange={setTarget}>
                                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-announce-target"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-72">
                                    <SelectItem value="all" data-testid="cc-announce-target-all">{t('commandCenter.act.announce.allResidents')}</SelectItem>
                                    {floors.length > 0 && (
                                        <SelectGroup>
                                            <SelectLabel>{t('commandCenter.act.announce.byFloor')}</SelectLabel>
                                            {floors.map((f) => (
                                                <SelectItem key={`floor-${f}`} value={`floor:${f}`} data-testid={`cc-announce-target-floor-${f}`}>
                                                    {t('commandCenter.act.announce.floor', { floor: f })}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs uppercase tracking-wide text-slate-500">{t('commandCenter.act.announce.message')}</Label>
                            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                                placeholder={t('commandCenter.act.announce.messagePlaceholder')}
                                className="bg-slate-800 border-slate-700" data-testid="cc-announce-message" />
                        </div>
                        <div data-testid="cc-announce-photo">
                            <PhotoAttach photos={photos} onChange={setPhotos}
                                helperText={t('commandCenter.act.announce.photoHelper')} />
                        </div>
                        <button type="button" onClick={() => setPinned((v) => !v)} data-testid="cc-announce-pin-toggle"
                            className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border transition-colors ${pinned ? 'border-amber-500/50 bg-amber-500/10 text-amber-300' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}>
                            <Pin className={`w-3.5 h-3.5 ${pinned ? 'fill-amber-300' : ''}`} />{t('commandCenter.act.announce.pin')}
                        </button>
                        <div className="pt-1 border-t border-slate-800">
                            <p className="text-xs font-semibold text-slate-400 mb-2 mt-3">{t('commandCenter.act.announce.recent')}</p>
                            {recent.length === 0 ? (
                                <p className="text-xs text-slate-500" data-testid="cc-announce-none">{t('commandCenter.act.announce.noRecent')}</p>
                            ) : (
                                <div className="space-y-2 max-h-56 overflow-y-auto">
                                    {recent.map((m) => (
                                        <div key={m.id} data-testid={`cc-announce-item-${m.id}`}
                                            className={`rounded-lg border p-2.5 text-xs ${m.recalled_at ? 'border-slate-800 bg-slate-800/30 opacity-60' : 'border-slate-700 bg-slate-800/60'}`}>
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <span className="font-semibold text-purple-300 inline-flex items-center gap-1.5">
                                                    {m.pinned && !m.recalled_at && (
                                                        <span className="inline-flex items-center gap-0.5 text-amber-300" data-testid={`cc-announce-pinned-${m.id}`}>
                                                            <Pin className="w-3 h-3 fill-amber-300" />{t('commandCenter.act.announce.pinnedBadge')}
                                                        </span>
                                                    )}
                                                    {scopeLabel(m)}
                                                </span>
                                                <span className="text-[11px] text-slate-500">{new Date(m.created_at).toLocaleString()}</span>
                                            </div>
                                            <p className="text-slate-200 whitespace-pre-wrap">{m.body}</p>
                                            {m.photo_ids?.length > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 mt-1" data-testid={`cc-announce-photo-badge-${m.id}`}>
                                                    <ImageIcon className="w-3 h-3" />{m.photo_ids.length}
                                                </span>
                                            )}
                                            <div className="flex items-center flex-wrap justify-between gap-2 mt-1.5">
                                                {m.recalled_at ? (
                                                    <span className="text-[11px] font-semibold text-red-300" data-testid={`cc-announce-recalled-${m.id}`}>{t('commandCenter.act.announce.recalledBadge')}</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-2">
                                                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300" data-testid={`cc-announce-reads-${m.id}`}>
                                                            <Eye className="w-3 h-3" />{t('commandCenter.act.announce.opened', { n: m.read_count || 0, total: m.audience_count || 0 })}
                                                        </span>
                                                        {m.pinned && (
                                                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-300" data-testid={`cc-announce-acks-${m.id}`}>
                                                                <Pin className="w-3 h-3" />{t('commandCenter.act.announce.acknowledged', { n: m.ack_count || 0, total: m.audience_count || 0 })}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                                {!m.recalled_at && (
                                                    <span className="inline-flex items-center gap-3">
                                                        <button type="button" onClick={() => togglePinItem(m)} data-testid={`cc-announce-pin-${m.id}`}
                                                            className="text-[11px] font-semibold text-amber-300 hover:text-amber-200 underline underline-offset-2">
                                                            {m.pinned ? t('commandCenter.act.announce.unpin') : t('commandCenter.act.announce.pin')}
                                                        </button>
                                                        <button type="button" onClick={() => setRecallItem(m)} data-testid={`cc-announce-recall-${m.id}`}
                                                            className="text-[11px] font-semibold text-red-300 hover:text-red-200 underline underline-offset-2">
                                                            {t('commandCenter.act.announce.recall')}
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cc-announce-cancel">{t('commandCenter.act.announce.cancel')}</Button>
                        <Button onClick={submit} disabled={sending || !message.trim()} data-testid="cc-announce-send"
                            className="bg-purple-600 hover:bg-purple-500 text-white">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Megaphone className="w-4 h-4 mr-1" />}
                            {sending ? t('commandCenter.act.announce.sending') : t('commandCenter.act.announce.send')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <AlertDialog open={!!recallItem} onOpenChange={(o) => { if (!o) setRecallItem(null); }}>
                <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cc-announce-recall-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('commandCenter.act.announce.recallConfirmTitle')}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{t('commandCenter.act.announce.recallConfirmBody')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-200" data-testid="cc-announce-recall-cancel">{t('commandCenter.act.announce.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={doRecall} disabled={recalling} data-testid="cc-announce-recall-confirm-btn"
                            className="bg-red-600 hover:bg-red-500 text-white">
                            {recalling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}{t('commandCenter.act.announce.recall')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
